import { type FC } from 'react';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';

import { getFormErrorAttributes } from '#apps/client-web/app/helpers/react-hook-form';
import {
  CeilingHeightLabel,
  ControlsLabel,
  SurfaceTypeLabel,
} from '#apps/client-web/features/audit-form-lighting-count/constants';

import {
  type CeilingHeight,
  type Controls,
  type SurfaceType,
} from '#packages/data-collection/models/audit-lighting-count-form';

import { type EditAuditFormLightingCountEntry } from './types';
import { type AuditLightingTypeFormEntry } from '#packages/data-collection/models/audit-lighting-type-form';
import {
  FixtureTypeLabel,
  LightSourceLabel,
} from '#apps/client-web/features/audit-form-lighting-type/constants';
import { isNil } from '#packages/data-collection/libs/lodash';

export type AuditFormLightingCountEntryPageRendererProps = {
  form: EditAuditFormLightingCountEntry;
  isPending: boolean;
  onSubmit: () => void;
  onOpenFixturePicker: () => void;
  selectedFixtureTypeEntry: AuditLightingTypeFormEntry | undefined;
};

export const AuditFormLightingCountEntryPageRenderer: FC<
  AuditFormLightingCountEntryPageRendererProps
> = ({
  form,
  isPending,
  onSubmit,
  onOpenFixturePicker,
  selectedFixtureTypeEntry,
}) => {
  const { control } = form;
  const disableForm = isPending;

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      display="flex"
      flexDirection="column"
      flex={1}>
      <Stack spacing={4} sx={{ mt: 2 }} flex={1}>
        {/* Section + Room */}
        <Grid container rowSpacing={4} columnSpacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
            <Controller
              control={control}
              name="section"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Section"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
            <Controller
              control={control}
              name="room"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Room"
                    type="text"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Fixture Type */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 4 }}>
            <Controller
              control={control}
              name="fixtureType"
              disabled={disableForm}
              render={({ fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    label="Fixture Type"
                    variant="outlined"
                    value={
                      !isNil(selectedFixtureTypeEntry?.fixtureType)
                        ? `${LightSourceLabel[selectedFixtureTypeEntry.lightSource!]} - ${
                            FixtureTypeLabel[
                              selectedFixtureTypeEntry?.fixtureType
                            ]
                          }`
                        : ''
                    }
                    onClick={disableForm ? undefined : onOpenFixturePicker}
                    slotProps={{
                      input: {
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <ChevronRight
                              sx={(theme) => ({
                                color: theme.palette.grey[400],
                              })}
                            />
                          </InputAdornment>
                        ),
                      },
                      htmlInput: { style: { cursor: 'pointer' } },
                    }}
                    placeholder="Select fixture type…"
                    {...getFormErrorAttributes(fieldState.error)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Fixture Quantity + Ceiling Height */}
          <Grid size={{ xs: 6, md: 3 }}>
            <Controller
              control={control}
              name="fixtureQuantity"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Fixture Quantity"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Controller
              control={control}
              name="ceilingHeight"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(CeilingHeightLabel)}
                    getOptionLabel={(option) =>
                      CeilingHeightLabel[option as CeilingHeight]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Ceiling Height"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Surface Type + Controls */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="surfaceType"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(SurfaceTypeLabel)}
                    getOptionLabel={(option) =>
                      SurfaceTypeLabel[option as SurfaceType]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Surface Type"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              control={control}
              name="controls"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <Autocomplete
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    options={Object.keys(ControlsLabel)}
                    getOptionLabel={(option) =>
                      ControlsLabel[option as Controls]
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Controls"
                        variant="outlined"
                        {...getFormErrorAttributes(fieldState.error)}
                      />
                    )}
                    value={field.value ?? null}
                    onChange={(_, value) => field.onChange(value)}
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          {/* Operation hrs/day + Operation days/week + Comments */}
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Controller
              control={control}
              name="operationHoursPerDay"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Operation (hrs/day)"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Controller
              control={control}
              name="operationDaysPerWeek"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Operation (days/week)"
                    type="number"
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Controller
              control={control}
              name="comments"
              disabled={disableForm}
              render={({ field, fieldState }) => (
                <FormControl
                  fullWidth
                  {...getFormErrorAttributes(fieldState.error)}>
                  <TextField
                    {...field}
                    {...getFormErrorAttributes(fieldState.error)}
                    label="Comments"
                    type="text"
                    multiline
                    rows={3}
                    variant="outlined"
                  />
                  <FormHelperText>{fieldState.error?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
